// ─── Shared ──────────────────────────────────────────────────────────────────

export type AgeBand = 'neutral' | 'amber' | 'red';

export const MANAGER_TAB_DEFAULT_PAGE_SIZE = 25;

export interface ManagerTabPaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filterBy?: string;
  filterValue?: string;
  countOnly?: boolean;
}

export interface ManagerTabPaginatedMeta {
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface SpecializationTag {
  id: number;
  name: string;
  type: string;
  displayText?: string | null;
}

// ─── Tab 1: Exception Queue ───────────────────────────────────────────────────

export interface ExceptionItem {
  applicationId: number;
  termPolicyId: number;
  carrierPolicyNumber: string | null;
  product: string | null;
  lifecycle: string | null;
  unassignedSince: string;
  ageHours: number;
  ageBand: AgeBand;
  blockingReason: string;
}

export interface ExceptionQueueResponse extends ManagerTabPaginatedMeta {
  exceptions: ExceptionItem[];
}

// ─── Tab 2: Team Capacity ─────────────────────────────────────────────────────

export interface TeamCapacityItem {
  userId: number;
  name: string | null;
  email: string;
  dailyPointCap: number;
  concurrentCaseCeiling: number;
  sodPlate: number;
  currentPlate: number;
  openCaseCount: number;
  status: 'active' | 'paused';
  specializations: SpecializationTag[];
  authorityLimit: SpecializationTag | null;
}

export interface TeamCapacityResponse {
  underwriters: TeamCapacityItem[];
}

export interface UpdateUwCapacityPayload {
  dailyPointCap?: number;
  concurrentCaseCeiling?: number;
  status?: 'active' | 'paused';
  lifecycleSpecIds?: number[];
  productSpecIds?: number[];
  authorityLimitId?: number | null;
}

export interface SpecializationOption {
  id: number;
  name: string;
  displayText?: string;
}

export interface SpecializationOptionsResponse {
  lifecycleOptions: SpecializationOption[];
  productOptions: SpecializationOption[];
  authorityLimitOptions: SpecializationOption[];
}

// ─── Tab 3: Smart Retry ───────────────────────────────────────────────────────

export interface SmartRetryItem {
  applicationId: number;
  termPolicyId: number;
  carrierPolicyNumber: string | null;
  product: string | null;
  lifecycle: string | null;
  holdSince: string | null;
  holdHours: number;
  missingEvidence: string[];
  retryCount: number;
}

export interface SmartRetryResponse extends ManagerTabPaginatedMeta {
  cases: SmartRetryItem[];
}

// ─── Tab 4: Bulk Assignment ───────────────────────────────────────────────────

export interface EligibleUwItem {
  id: number;
  name: string | null;
  email: string;
  currentPlate: number;
  dailyPointCap: number;
  status: 'active' | 'paused';
  specializations: SpecializationTag[];
  authorityLimit: SpecializationTag | null;
}

export interface EligibleUwsResponse {
  underwriters: EligibleUwItem[];
}

export interface BulkCaseResult {
  applicationId: number;
  success: boolean;
  reason?: string;
}

export interface BulkCaseAssignmentPayload {
  applicationIds: number[];
  targetUwId: number;
  overrideReason: string;
}

export interface BulkCaseAssignmentResponse {
  results: BulkCaseResult[];
  successCount: number;
  failureCount: number;
}
